/* eslint-disable no-underscore-dangle */
import React, { useContext } from 'react';
import styled from 'styled-components';
import { TemplateInputType } from '@bb/domain/dist/types';
import { FeedbackController } from 'api';
import { Modal, Tooltip, breakpoints } from 'components';
import { AuthContext, LayoutContext, FeedbackModalContext } from 'contexts';
import {
  Button,
  message,
  Spin,
  Text,
  VerticalSpace,
} from '@brokerbay/design-components';
import { FeedbackTemplate } from '@bb/domain/dist/types/Feedback';
import { Feedback } from 'api/FeedbackController/getFeedback';
import {
  FeedbackCheckbox,
  FeedbackRadio,
  FeedbackSelect,
  FeedbackText,
} from './inputs';
import { FeedbackInputProps } from './types';
import { FEEDBACK_MODAL_ZINDEX } from './constants';
import { StyledTooltipButtonWrapper } from '../../Router/pages/TourEditPage/StyledComponents';

const FEEDBACK_INPUT_MAP: Record<
  TemplateInputType,
  React.FC<FeedbackInputProps>
> = {
  checkbox: FeedbackCheckbox,
  radio: FeedbackRadio,
  select: FeedbackSelect,
  text: FeedbackText,
};

type FeedbackState = {
  id?: string;
  appointment: string;
  listing: string;
  contactId: string;
};

export const FeedbackModal = () => {
  const {
    appointmentId,
    modalType,
    modalMode,
    listingId,
    closeModal,
    visible,
  } = useContext(FeedbackModalContext);

  const { isMobileView } = useContext(LayoutContext);

  const { auth } = useContext(AuthContext);

  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);

  const [feedback, setFeedback] = React.useState<FeedbackState>();
  const [feedbackTemplate, setFeedbackTemplate] =
    React.useState<Feedback['template']>();

  const submitDisabled = React.useMemo(() => {
    if (submitting) {
      return true;
    }
    return !feedbackTemplate?.some(template => !!template.response);
  }, [submitting, feedbackTemplate]);

  const fetchFeedback = React.useCallback(async () => {
    setLoading(true);

    if (modalType === 'coop') {
      const result = await FeedbackController.getFeedbackForCoop({
        listingId,
        appointmentId,
      });

      setFeedbackTemplate(result.template);
    }
    if (modalType === 'listing') {
      const result = await FeedbackController.getFeedback({
        listingId,
        appointmentId,
      });

      setFeedback(result);
      setFeedbackTemplate(result.template);
    }

    setLoading(false);
  }, [appointmentId, listingId, modalType]);

  const fetchFeedbackSettings = React.useCallback(async () => {
    setLoading(true);

    const result = await FeedbackController.getFeedbackSettings({
      listingId,
    });

    setFeedback({
      appointment: appointmentId,
      listing: listingId,
      contactId: auth.id,
    });

    setFeedbackTemplate(result.template);

    setLoading(false);
  }, [appointmentId, listingId, auth.id]);

  React.useEffect(() => {
    if (appointmentId && listingId) {
      if (modalType === 'coop') fetchFeedback();
      if (modalType === 'listing') {
        if (modalMode === 'record') fetchFeedbackSettings();
        if (modalMode === 'view') fetchFeedback();
      }
    }
  }, [
    appointmentId,
    fetchFeedback,
    modalMode,
    modalType,
    fetchFeedbackSettings,
    listingId,
  ]);

  const handleClose = () => {
    closeModal();
  };

  const onResponseChange = (index: number) => (response: string) => {
    const newFeedbackTemplate = [...feedbackTemplate];
    newFeedbackTemplate[index].response = response;
    setFeedbackTemplate(newFeedbackTemplate);
  };

  const buildFeedbackInput = (index: number, template: FeedbackTemplate) => {
    const Element = FEEDBACK_INPUT_MAP[template.type];
    return (
      <Element
        disabled={modalMode === 'view'}
        index={index}
        onChange={onResponseChange(index)}
        template={template}
      />
    );
  };

  const footer = () => (
      <ButtonContainer>
        <Button onClick={() => closeModal()}>Cancel</Button>
        <Tooltip
          title={
            submitDisabled ? 'Please answer at least one feedback question' : ''
          }
        >
          <StyledTooltipButtonWrapper>
            <Button
              disabled={submitDisabled}
              loading={submitting}
              type="primary"
              onClick={_handleSubmit}
            >
              Submit
            </Button>
          </StyledTooltipButtonWrapper>
        </Tooltip>
      </ButtonContainer>
    );

  const _handleSubmit = async () => {
    if (submitting) return;

    setSubmitting(true);

    if (modalType === 'coop') {
      await FeedbackController.saveFeedbackByCoop({
        appointmentId,
        listingId,
        feedback: {
          ...feedback,
          template: [...feedbackTemplate],
        },
      });
      message.success('Feedback submitted');
    }

    if (modalType === 'listing') {
      await FeedbackController.saveFeedback({
        appointmentId,
        listingId,
        feedback: {
          ...feedback,
          template: [...feedbackTemplate],
        },
      });
      // Delay for eyeball modal to close
      setTimeout(() => message.success('Feedback recorded'), 1500);
    }

    setSubmitting(false);
    closeModal({ resetModal: true, fireSubmit: true });
  };

  return (
    <StyledModal
      centered
      open={visible}
      onCancel={handleClose}
      cancelText="Close"
      title="Give Feedback"
      footer={modalMode === 'record' ? footer() : <br />}
      width={breakpoints.sm}
      zIndex={FEEDBACK_MODAL_ZINDEX}
      destroyOnClose
      isMobileView={isMobileView}
    >
      {loading ? (
        <StyledSpinnerContainer>
          <Spin />
        </StyledSpinnerContainer>
      ) : (
        <FeedbackContainer>
          {modalType === 'coop' && (
            <CTAContainer>
              <Text>
                We would really appreciate your client&apos;s feedback. Please
                fill in the form below and add any additional comments you may
                have. Thank you in advance!
              </Text>
            </CTAContainer>
          )}
          <VerticalSpace size="large">
            {feedbackTemplate.map((template, index) => (
              <FeedbackInputContainer key={index}>
                <FeedbackLabel>{template.label}</FeedbackLabel>
                {buildFeedbackInput(index, template)}
              </FeedbackInputContainer>
            ))}
          </VerticalSpace>
        </FeedbackContainer>
      )}
    </StyledModal>
  );
};

const StyledModal = styled(Modal).attrs({
  bodyStyle: { overflowY: 'auto', maxHeight: '70vh' },
})<{
  isMobileView?: boolean;
}>`
  ${({ isMobileView }) =>
    isMobileView ? 'margin: 0px; padding: 0px; max-width: 100vw;' : ''};
`;

const FeedbackContainer = styled.div`
  margin-bottom: 12px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const StyledSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 450px;
`;

const FeedbackInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  && textarea,
  span {
    color: rgba(0, 0, 0, 0.85);
  }
`;

const FeedbackLabel = styled(Text)`
  margin-bottom: 8px;
`;

const CTAContainer = styled.div`
  margin-bottom: 12px;
`;
